// @ts-nocheck
import React, { useRef, useCallback, useState, useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';

import Skeleton from 'react-loading-skeleton';
import { DownArrowBold, SleepingEmoji } from '../../assets/svg';

import {
  AMOUNT_CELL_NUMBER,
  NO_TRANSACTIONS,
  STATUS_CELL_NUMBER,
  TABLE_STYLES,
  TRANSACTION_TYPES,
} from '../../flows/transactions/constants';
import { useStore } from '../../store';
import { addDotsWithNullIfNeeded, checkAmountSign } from '../../helpers/currency.helper';
import { useSortableTable } from '../../hooks/useSortableTable';
import Icon from '../Icon';
import 'react-loading-skeleton/dist/skeleton.css';
import { IAdditionalTransactionsStates, ITrxData } from '../../types';
import { getTransactionStatus } from '../../flows/transactions/utils';

export interface IHeader {
  title: string;
  value: string;
  headStyle?: string;
  tdStyles?: string;
  tdSelectedStyle?: string;
}

interface IHeaders {
  headers: IHeader[];
  items: {}[];
  tableType: 'unsortable' | 'sortable';
  handleLastItemIntersecting?: () => void;
  onItemClick?: (item: any) => any;
  additionalStates?: IAdditionalTransactionsStates;
  isSuccess: boolean;
  isLoading: boolean;
  isFetching?: boolean;
}

export const Table = ({
  headers,
  items,
  handleLastItemIntersecting,
  onItemClick,
  tableType,
  additionalStates,
  isSuccess,
  isLoading,
  isFetching,
}: IHeaders) => {
  const { transactionId: currentTransactionId } = useStore((state) => state.activeTransaction);
  const [handleSorting, sortingIndex, order] = useSortableTable(items);
  const [isInitialRender, setIsInitialRender] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    if (isInitialRender) {
      setIsInitialRender(false);
    }
  }, [items]);

  const watcher = useRef<HTMLElement>();
  const lastRow = useCallback(
    (callback) => {
      if (watcher.current) watcher.current.disconnect();
      watcher.current = new IntersectionObserver(
        (entry, o) => {
          if (entry[0].isIntersecting) {
            handleLastItemIntersecting();
            o.unobserve(entry[0].target);
          }
        },
        { rootMargin: '100px', root: null, threshold: 1 },
      );
      if (callback) watcher.current.observe(callback);
    },
    [items],
  );

  const getStatus = (trxData: ITrxData) => getTransactionStatus(trxData, t);

  const handleRowClassCondition = (headerValue: string, cellValue: any, trxData?: ITrxData) => {
    switch (headerValue) {
      case 'amount':
        if (trxData && getStatus(trxData) === TRANSACTION_TYPES.CHECK_STATUS.REJECTED) return 'text-bw06';
        return checkAmountSign(cellValue.toString()) === 1 ? 'text-badgeGreen03' : 'text-bw08';
      case 'transactionId':
        return cellValue === currentTransactionId ? TABLE_STYLES.TABLE_ROW_ACTIVE : TABLE_STYLES.TABLE_ROW;
      case 'status':
        if (trxData && getStatus(trxData) === TRANSACTION_TYPES.CHECK_STATUS.REJECTED) return 'text-bw06';
        return '';
      default:
        return '';
    }
  };

  const getUniqueKey = () => useId();
  return (
    <table className='w-full rounded border-separate relative bg-bw00 rounded-lg select-none'>
      <thead className={TABLE_STYLES.TABLE_THEAD}>
        <tr className={TABLE_STYLES.TABLE_HEADER}>
          {headers.map((header, index) => (
            <th
              id={`th${t(header.title)}`}
              className={`${TABLE_STYLES.TABLE_HEADER_TITLE} ${header.headerStyle} ${tableType === 'sortable' && index === sortingIndex ? 'text-bw08' : 'text-bw07'}`}
              onClick={() => {
                if (tableType === 'sortable') {
                  handleSorting(header, index);
                }
              }}
              key={header.title}
            >
              {t(header.title)}
              {tableType === 'sortable' && index === sortingIndex ? (
                order === 'asc' ? (
                  <Icon src={DownArrowBold} />
                ) : (
                  <Icon src={DownArrowBold} className='rotate-180' />
                )
              ) : (
                ''
              )}
            </th>
          ))}
        </tr>
        <tr className='flex justify-center px-[3.25rem]'>
          <td className='w-full'>
            <div className='w-full border-b border-bw05' />
          </td>
        </tr>
      </thead>

      {isLoading || (isFetching && items.length === 0) ? (
        <tbody>
          <tr>
            <th>
              <Skeleton className='my-4 h-4' count={11} />
            </th>
          </tr>
        </tbody>
      ) : (
        <tbody className={TABLE_STYLES.TABLE_BODY}>
          {items.map((item, itemIdx) => {
            const itemProps = itemIdx === items.length - 1 ? { ref: lastRow } : {};
            return (
              <tr
                id={`tr${itemIdx}`}
                className={handleRowClassCondition('transactionId', item.transactionId, item)}
                onClick={() => {
                  if (onItemClick) onItemClick(item);
                }}
                key={`${item.transactionId}${getUniqueKey}`}
                {...itemProps}
              >
                {headers.map((head, headerIdx) => {
                  const cellValue = item[headers[headerIdx].value] || '';
                  return (
                    <td
                      className={`${handleRowClassCondition(head.value, cellValue, item)} ${TABLE_STYLES.TABLE_CELL} ${head.tdStyle} ${currentTransactionId === item.transactionId && headerIdx === 0 ? 'font-metricMedium text-bw08' : ''}`}
                      key={`${cellValue}${getUniqueKey}`}
                      id={`tr${itemIdx}${t(head.title)}`}
                    >
                      {headerIdx === AMOUNT_CELL_NUMBER
                        ? addDotsWithNullIfNeeded(cellValue)
                        : headerIdx === STATUS_CELL_NUMBER
                          ? getStatus({
                            trxType: item?.trxType,
                            externalTransactionType: item?.externalTransactionType,
                            trxTypeParams: item?.trxTypeParams,
                            transactionStatus: cellValue,
                            checkDetailsData: item?.checkDetailsData,
                          })
                          : cellValue}
                    </td>
                  );
                })}
              </tr>
            );
          })}
          {additionalStates?.noTransactionsAtAll && isSuccess && isInitialRender && (
            <tr className='w-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 '>
              <td className='text-center flex flex-col justify-center items-center'>
                <Icon src={SleepingEmoji} className='h-[155px] w-[147px] pb-6' />
                <p className='text-bw06 font-metricMedium'>{t('TRANSACTIONS_PAGE.EMPTY_STATE_TEXT')}</p>
              </td>
            </tr>
          )}
          {/* no filtering results state or no more transactions to load after pagination state */}
          {!isFetching && items.length === 0 && !isInitialRender && !isLoading ? (
            <tr className='w-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 '>
              <td className='text-center flex flex-col justify-center items-center'>
                <p data-testid='zeroStateText' className='text-bw06 font-metricMedium'>
                  {t('TRANSACTIONS_PAGE.ZERO_STATE_TEXT')}
                </p>
              </td>
            </tr>
          ) : additionalStates?.endOfTransactions && !isFetching ? (
            <tr className='w-full flex justify-center items-center pb-2.5'>
              <td className='text-bw06 font-metricMedium'>{t(NO_TRANSACTIONS)}</td>
            </tr>
          ) : (
            ''
          )}
        </tbody>
      )}
    </table>
  );
};
